import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, EMPTY, from, Observable, of, Subject, throwError } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  finalize,
  last,
  map,
  shareReplay,
  switchMap,
  tap,
  timeout,
} from "rxjs/operators";

import { ApiService } from "@bitwarden/common/abstractions/api.service";

import { getGroceryConfig } from "./grocery.config";
import { GROCERY_TIMEOUT_MS, withGroceryAuth } from "./grocery-http";
import {
  ComparisonSummary,
  GroceryItem,
  GroceryItemsResponse,
  GroceryResultRow,
  GroceryStoreKey,
  PricePersistResult,
  ShoppingRow,
} from "./grocery.types";
import { buildResultRow, buildResultRows, buildSummary, listItemDedupKey } from "./grocery.utils";
import { ShoppingListService } from "./shopping-list.service";
import { NewShoppingListItem, ShoppingList, ShoppingListItem } from "./shopping-list.types";

/** Minimum query length before the catalog is searched. */
const MIN_QUERY_LENGTH = 2;
/** Page size requested per search call (matches the backend's default). */
export const PAGE_SIZE = 15;

/** One page of search results, decorated for rendering. */
export interface SearchPageResult {
  rows: GroceryResultRow[];
  /** True when more results exist beyond this page (drives "Show more"). */
  hasMore: boolean;
  /** Total matches for the query (after server-side exclusions) — drives the "N matches" copy. */
  total: number;
}

/**
 * Load status of the active list, so the view can tell apart the three states
 * that {@link GroceryService.getList} previously collapsed into an empty array:
 * `"loading"` (never loaded / a load in flight), `"loaded"` (a list is in state —
 * possibly a genuinely empty one), and `"error"` (a load failed and no list is
 * held). Without this, a first-open flash and a failed load both render as "your
 * list is empty".
 */
export type GroceryListStatus = "loading" | "loaded" | "error";

/** Maps a stored list item to the GroceryItem shape the comparison maths use. */
function toGroceryItem(item: ShoppingListItem): GroceryItem {
  return {
    // Custom items have no catalog id — key them by their list-item id so the
    // decoration/track-by has a stable, unique value.
    id: item.genericItemId ?? item._id,
    name: item.name,
    category: item.category ?? "",
    quantity: item.quantity ?? 0,
    unit: (item.unit as GroceryItem["unit"]) ?? "ea",
    prices: item.prices ?? {},
    priceAsOf: item.priceAsOf,
    sourceUrls: item.sourceUrls,
  };
}

/** Maps a stored list item to the input shape for creating/copying it. */
function toNewItem(item: ShoppingListItem): NewShoppingListItem {
  return {
    genericItemId: item.genericItemId,
    name: item.name,
    category: item.category,
    quantity: item.quantity,
    unit: item.unit,
    prices: item.prices,
    priceAsOf: item.priceAsOf,
    sourceUrls: item.sourceUrls,
  };
}

/**
 * Backing data source for the Gloss Grocery page.
 *
 * Search is on-demand and server-side. The shopping "list" is now a persistent,
 * per-user list stored by gloss-grocery (via {@link ShoppingListService}); this
 * service holds the current active list as reactive state and mirrors every
 * mutation (add/remove/tick/archive/copy) to the backend, so the list survives
 * a refresh and syncs across devices. The component talks only to this service.
 *
 * The active list is loaded on demand via {@link refreshActiveList} — the
 * component calls it on page open, not the constructor — so a transient load
 * failure is retried the next time the page is opened (rather than leaving the
 * singleton stuck with a null list for the whole session) and a re-open picks
 * up a list changed on another device / after an account switch.
 */
@Injectable({
  providedIn: "root",
})
export class GroceryService {
  /** The user's current (active) saved list — null until loaded / between lists. */
  private readonly activeList$ = new BehaviorSubject<ShoppingList | null>(null);

  /**
   * Load status of {@link activeList$}, exposed via {@link getStatus}. A null
   * list alone can't tell "still loading" from "load failed" — both are null —
   * so the view needs this to render a loading state, an empty state, and an
   * error state as three different things rather than one "empty" message.
   */
  private readonly status$ = new BehaviorSubject<GroceryListStatus>("loading");

  /**
   * Serialises active-list loads: each {@link refreshActiveList} ticks this, and
   * `switchMap` cancels any in-flight getLists/createList so overlapping page
   * opens cannot double-create a first list or let a stale response overwrite a
   * newer one.
   */
  private readonly load$ = new Subject<void>();

  /**
   * The in-flight "this user has no list yet" create, shared across overlapping
   * loads. Held here rather than left to `switchMap` because cancelling a POST
   * that already reached the server does not un-create the list: a newer load
   * would find none and create a second one. Kept after a successful create so a
   * racing getLists that still sees [] joins the replay instead of POSTing
   * again; cleared on error (so a failed create can retry) and once getLists
   * returns a list (so a later archive can create afresh).
   */
  private createInFlight$: Observable<ShoppingList> | null = null;

  /**
   * Bumped as each mutation is *issued*, to guard the **load** path only: a load
   * captures it when it starts and discards its snapshot if it moved, because
   * that snapshot may be a read the server took before the mutation — applying it
   * wholesale would drop a just-added item or restore a just-removed one, and the
   * page-open reload makes that window ordinary (the list renders from memory
   * straight away, so a tap can land while the reload is still out). Nothing is
   * lost by discarding — every mutation response carries the whole updated list.
   * Mutation-vs-mutation ordering is handled separately by {@link mutations$}.
   */
  private mutationGeneration = 0;

  /**
   * Serialises list mutations (add / custom-add / remove / tick) so their
   * responses apply in the order the writes were issued. Each write returns the
   * whole list, so firing them on independent subscriptions let an out-of-order
   * response resurrect a just-removed row or drop a just-added one (#5090).
   * Piping every mutation through one `concatMap` means the next write is not
   * issued until the previous one's response has been applied, so responses
   * cannot reorder — and, because each earlier write is applied before the next
   * is even sent, a later failure can no longer discard an earlier success.
   * Loads stay off this queue (guarded by {@link mutationGeneration}).
   */
  private readonly mutations$ = new Subject<{
    /**
     * The active list's `_id` when this write was enqueued. The queue skips the
     * write at send time if the active list has since been archived or replaced,
     * so a still-queued add can't be POSTed to — and its response can't resurrect
     * — a list the user has already moved on from.
     */
    listId: string;
    run: () => Observable<ShoppingList>;
    /** Ran on a successful response, before the list is pushed into state. */
    onApply?: (list: ShoppingList) => void;
    /** Prefix for the console.error if the write fails. */
    errorLabel: string;
    /**
     * Reports the write's outcome to a caller that needs it (the queue otherwise
     * swallows failures): `done` once the response is applied, `fail` if the
     * write is skipped, dropped as stale, or errors.
     */
    settle?: { done: (list: ShoppingList) => void; fail: (error: unknown) => void };
  }>();

  /** The `excludeIds` snapshot for the query currently being paged (see search). */
  private pagedExcludeIds = "";

  /**
   * Session overrides for prices fetched via a live refresh, keyed by catalog
   * (generic) item id. Re-applied whenever the active list is replaced from the
   * server (add/remove/load), so a just-refreshed price doesn't revert: the
   * refresh is stored in the shared catalog separately (see {@link persistPrices}),
   * not on the list document, so the server list carries the pre-refresh price.
   */
  private readonly priceOverrides = new Map<
    string,
    {
      prices: Partial<Record<GroceryStoreKey, number>>;
      priceAsOf: Partial<Record<GroceryStoreKey, string>>;
    }
  >();

  /** Terms already reported as not-found this session, so each is counted once. */
  private readonly reportedNotFound = new Set<string>();

  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService,
    private readonly lists: ShoppingListService,
  ) {
    this.load$
      .pipe(
        switchMap(() => {
          const generation = this.mutationGeneration;
          return this.lists.getLists("active").pipe(
            switchMap((lists) => {
              // Bail before creating anything if a mutation has been issued since
              // this load started: archive bumps the generation up-front but only
              // publishes its follow-on create once its PATCH returns, so a GET
              // that lands [] during that whole round-trip would otherwise POST a
              // duplicate active list here (the trailing filter drops the result,
              // but not the POST). Skipping the create — and the createInFlight$
              // reset — leaves the in-flight archive's create untouched.
              if (generation !== this.mutationGeneration) {
                return EMPTY;
              }
              if (lists.length > 0) {
                // Server has a list — drop any replay so a later empty getLists
                // (archive in 6/9) POSTs a fresh one instead of replaying this.
                this.createInFlight$ = null;
                // Prefer the list already held: "Archive this item" briefly
                // leaves a second, newer active list server-side (and a failed
                // rollback can leave it there), which lists[0] would pick.
                const heldId = this.activeList$.value?._id;
                return of(lists.find((l) => l._id === heldId) ?? lists[0]);
              }
              return this.createActiveList();
            }),
            filter(() => generation === this.mutationGeneration),
            catchError((error: unknown) => {
              // Per-attempt so a failed load does not complete this pipeline —
              // the next page-open can retry. Emits nothing rather than null:
              // the load now runs on every page open, so pushing null would let
              // one flaky reload blank out a list the user already had (and drop
              // every subsequent add) until a later open happened to succeed.
              // eslint-disable-next-line no-console
              console.error("GroceryService: failed to load the active list", error);
              // Surface the failure so the view can show an error rather than an
              // empty list. Harmless when a list is already held (a reload that
              // failed): the view keys "error" off there being no list to show.
              this.status$.next("error");
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe((list) => {
        this.setActiveList(list);
        this.status$.next("loaded");
      });

    // Drain the mutation queue one write at a time (see mutations$). `concatMap`
    // keeps the next write buffered until the current one's response is applied.
    this.mutations$
      .pipe(
        concatMap((mutation) => {
          // Skip a write whose target list was archived or replaced while it
          // waited in the queue (archiveActiveAndStartNew / startNewActiveList /
          // copy write straight to setActiveList, off-queue). Sending it would
          // POST to the stale list — silently creating an item on the now-archived
          // list — and its response would resurrect that list. Skipping avoids
          // both, and is safe because the write never needed to reach that list.
          if (mutation.listId !== this.activeList$.value?._id) {
            // Surface the discard: unlike the failure path below this would
            // otherwise be silent, and a custom item added just before an archive
            // vanishes without a trace.
            // eslint-disable-next-line no-console
            console.error(
              "GroceryService: skipped a queued list write — the active list was archived or replaced before it was sent",
            );
            // A racing page-open reload may have set "loading" and then been
            // discarded by the generation bump; with the write skipped, nothing
            // else emits to clear it — re-resolve against the list we still hold.
            this.resolveStatus();
            mutation.settle?.fail(new Error("active list changed before the write was sent"));
            return EMPTY;
          }
          // Bump at send time too (not only when queued in enqueueMutation): a
          // page-open reload that started while this write was still buffered
          // behind an earlier one captured the pre-send generation, so without a
          // second bump its stale snapshot would pass the load guard and land
          // after this write — resurrecting a queued-removed row (#5090).
          this.mutationGeneration++;
          // Guarantees the caller's settle fires exactly once: if the write
          // completes without emitting or erroring, the `finalize` below reports
          // it, so a waiting caller can't hang (and keep its UI lock held).
          let settled = false;
          const settle = mutation.settle && {
            done: (list: ShoppingList) => {
              settled = true;
              mutation.settle?.done(list);
            },
            fail: (error: unknown) => {
              settled = true;
              mutation.settle?.fail(error);
            },
          };
          return mutation.run().pipe(
            tap((updated) => {
              // The list was archived or replaced *after* this write had already
              // been sent (the skip above only catches still-queued writes), so
              // its response carries the old list: drop it rather than overwrite
              // the fresh active list, and re-resolve status as on the skip path.
              if (updated._id !== this.activeList$.value?._id) {
                this.resolveStatus();
                settle?.fail(new Error("active list changed while the write was in flight"));
                return;
              }
              mutation.onApply?.(updated);
              this.setActiveList(updated);
              settle?.done(updated);
            }),
            catchError((error: unknown) => {
              // Swallow so one failed write does not tear down the queue and
              // stall every later mutation. The list is left as the last applied
              // response; surfacing the failure to the user is tracked in #4993.
              // eslint-disable-next-line no-console
              console.error(mutation.errorLabel, error);
              // The send-time generation bump discards an in-flight page-open
              // reload; a failed write leaves the held list intact but never
              // calls setActiveList, so re-resolve here too rather than stranding
              // the "loading" refreshActiveList set behind the loading spinner.
              this.resolveStatus();
              settle?.fail(error);
              return EMPTY;
            }),
            finalize(() => {
              if (!settled) {
                settle?.fail(new Error("write finished without a response"));
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  /**
   * Enqueues a list-mutating write onto {@link mutations$} so it runs after any
   * already-queued writes. Bumps {@link mutationGeneration} up-front (as the old
   * per-mutation code did) so an in-flight page-open reload discards its stale
   * snapshot; the write's own response is applied in order by the queue.
   */
  private enqueueMutation(
    listId: string,
    run: () => Observable<ShoppingList>,
    errorLabel: string,
    onApply?: (list: ShoppingList) => void,
    settle?: { done: (list: ShoppingList) => void; fail: (error: unknown) => void },
  ): void {
    this.mutationGeneration++;
    this.mutations$.next({ listId, run, onApply, errorLabel, settle });
  }

  /**
   * {@link enqueueMutation} as a cold observable: the write is enqueued on
   * subscribe and the observable emits the updated list once the queue has
   * applied it, or errors if the write fails or is dropped.
   */
  private enqueueAndWait(
    listId: string,
    run: () => Observable<ShoppingList>,
    errorLabel: string,
    onApply?: (list: ShoppingList) => void,
  ): Observable<ShoppingList> {
    return new Observable<ShoppingList>((subscriber) =>
      this.enqueueMutation(listId, run, errorLabel, onApply, {
        done: (updated) => {
          subscriber.next(updated);
          subscriber.complete();
        },
        fail: (error) => subscriber.error(error),
      }),
    );
  }

  /**
   * Loads the most-recent active list, or creates one if the user has none.
   * Called by the component on page open (not in the constructor) so a failed
   * load is retried on the next open instead of leaving this root singleton with
   * a null list for the rest of the session. Overlapping calls cancel the
   * in-flight one rather than stacking subscriptions.
   */
  refreshActiveList(): void {
    // Mark loading before the request goes out. Only changes what the view shows
    // while the list is null (initial open); a reopen with a list already in
    // state keeps rendering it, so this doesn't reintroduce the empty flash.
    this.status$.next("loading");
    this.load$.next();
  }

  /** The active list's load status — see {@link GroceryListStatus}. */
  getStatus(): Observable<GroceryListStatus> {
    return this.status$.asObservable();
  }

  /**
   * Creates the user's first list, reusing an in-flight create if one is already
   * out. `shareReplay` with `refCount: false` keeps the POST subscribed after
   * `switchMap` drops it, so a page open that races the create joins it instead
   * of issuing a second one (which would leave two active lists server-side,
   * with no UI to see or reconcile them). Not cleared on success — a GET that
   * raced the POST can still see [] after it settles, and must join this replay.
   */
  private createActiveList(): Observable<ShoppingList> {
    this.createInFlight$ ??= this.lists.createList().pipe(
      tap({
        error: () => (this.createInFlight$ = null),
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.createInFlight$;
  }

  /**
   * Pushes a server list into state with any session price overrides re-applied,
   * so a live-refreshed price survives a later add/remove that replaces the list
   * wholesale with the server's (pre-refresh) copy.
   */
  private setActiveList(list: ShoppingList | null): void {
    this.activeList$.next(list ? this.withPriceOverrides(list) : null);
    // Resolve the status here rather than only in the load subscribe: a load
    // discarded by the mutation-generation filter emits nothing, so the
    // mutation response that superseded it is what has to clear "loading".
    this.status$.next(list ? "loaded" : "loading");
  }

  /**
   * Clears a stale "loading" for the list currently held, without touching the
   * list itself. Used on the mutation queue's skip / stale-response-drop /
   * failed-write paths: those exit without calling {@link setActiveList}, so if a
   * racing page-open reload set "loading" and was then discarded by the
   * generation guard, nothing else would clear it and the view would hide the
   * list behind the loading spinner.
   *
   * A no-op when no list is held: in that case whatever the load/archive path
   * last set is the accurate status (a failed load, or an archive whose create
   * failed and set "error"), and forcing "loading" here would reinstate exactly
   * the permanent spinner that error handling exists to prevent.
   */
  private resolveStatus(): void {
    if (this.activeList$.value) {
      this.status$.next("loaded");
    }
  }

  private withPriceOverrides(list: ShoppingList): ShoppingList {
    if (this.priceOverrides.size === 0) {
      return list;
    }
    return {
      ...list,
      items: list.items.map((it) => {
        const override = it.genericItemId ? this.priceOverrides.get(it.genericItemId) : undefined;
        return override
          ? {
              ...it,
              prices: { ...(it.prices ?? {}), ...override.prices },
              priceAsOf: { ...(it.priceAsOf ?? {}), ...override.priceAsOf },
            }
          : it;
      }),
    };
  }

  /**
   * Fetch one page of catalog results for `query`. Items already on the active
   * list are excluded server-side; the exclude set is snapshotted on the first
   * page (offset 0) and reused for subsequent pages of the same query. Failures
   * are surfaced (never masked as an empty page).
   */
  search(query: string, offset = 0): Observable<SearchPageResult> {
    const trimmed = (query ?? "").trim().toLowerCase();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      return of({ rows: [], hasMore: false, total: 0 });
    }

    if (offset === 0) {
      const active = this.activeList$.value;
      this.pagedExcludeIds = active
        ? active.items
            .map((i) => i.genericItemId)
            .filter((id): id is string => !!id)
            .join(",")
        : "";
    }
    const excludeIds = this.pagedExcludeIds;
    let params = new HttpParams()
      .set("q", trimmed)
      .set("offset", String(offset))
      .set("limit", String(PAGE_SIZE));
    if (excludeIds) {
      params = params.set("excludeIds", excludeIds);
    }

    return withGroceryAuth(this.apiService, (headers) =>
      this.http.get<GroceryItemsResponse>(
        `${getGroceryConfig().url}${getGroceryConfig().endpoint.items}`,
        { params, headers },
      ),
    ).pipe(
      map((res) => ({
        // The component recomputes isBestValue itself over every accumulated
        // page (see GlossGroceryComponent.runSearch$), so a page-local
        // buildResultRows() best-value pass here would be wasted work.
        rows: res.items.map(buildResultRow),
        hasMore: offset + res.items.length < res.total,
        total: res.total,
      })),
      catchError((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error("GroceryService: search request failed", error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * The active list as render-ready rows (with tick/remove metadata), or `null`
   * when no list is loaded. Preserving `null` (rather than collapsing it to `[]`)
   * lets the view distinguish "no list yet / load failed" from a genuinely empty
   * list — see {@link getStatus}.
   */
  getList(): Observable<ShoppingRow[] | null> {
    return this.activeList$.pipe(map((list) => (list ? this.buildListRows(list.items) : null)));
  }

  /**
   * Whole-of-list comparison summary, emitting on every list change. Null only when
   * the list has no catalog items to compare — an empty list, or an all-custom/unlisted
   * list (custom items are filtered out below). A list of catalog items that all
   * currently lack prices still emits the honest "no single store carries everything"
   * summary (every store lists them as missing), rather than hiding the card.
   */
  getSummary(): Observable<ComparisonSummary | null> {
    return this.activeList$.pipe(
      map((list) => {
        // Compare catalog items only; custom/unlisted items (genericItemId == null)
        // carry no catalog price and aren't part of the store comparison. Filter on
        // the honest signal HERE so buildSummary still sees priceless *catalog* items
        // — a store that lacks them must count as not carrying everything.
        const items = (list?.items ?? []).filter((i) => i.genericItemId != null).map(toGroceryItem);
        return items.length > 0 ? buildSummary(items) : null;
      }),
    );
  }

  /** The current active list (for the header name + save button). */
  getActiveList(): Observable<ShoppingList | null> {
    return this.activeList$.asObservable();
  }

  /** Rename (save a nickname for) the active list. */
  renameActive(name: string): Observable<ShoppingList> {
    const list = this.activeList$.value;
    if (!list) {
      return throwError(() => new Error("no active list"));
    }
    // Bump before the async write (like add/remove/tick) so an in-flight
    // page-open reload can't apply a pre-rename GET and revert the nickname.
    this.mutationGeneration++;
    return this.lists
      .updateList(list._id, { name })
      .pipe(tap((updated) => this.setActiveList(updated)));
  }

  private buildListRows(items: ShoppingListItem[]): ShoppingRow[] {
    const rows = buildResultRows(items.map(toGroceryItem));
    return rows.map((row, i) => ({
      ...row,
      listItemId: items[i]._id,
      purchased: items[i].purchased,
      isCustom: items[i].genericItemId == null,
    }));
  }

  /**
   * Adds a catalog item to the active list (deduped by catalog id). Returns
   * `false` when there is no active list yet (still loading / a failed load),
   * so the caller can keep the user's search context instead of clearing it on
   * an add that was silently dropped; `true` when the item is (or already was)
   * on the list.
   */
  addItem(item: GroceryItem): boolean {
    const list = this.activeList$.value;
    if (!list) {
      return false;
    }
    if (list.items.some((i) => i.genericItemId === item.id)) {
      return true;
    }
    this.enqueueMutation(
      list._id,
      () =>
        this.lists.addItem(list._id, {
          genericItemId: item.id,
          name: item.name,
          category: item.category,
          quantity: item.quantity,
          unit: item.unit,
          prices: item.prices,
          priceAsOf: item.priceAsOf,
          sourceUrls: item.sourceUrls,
        }),
      "GroceryService: add item failed",
    );
    return true;
  }

  /**
   * Adds a custom/unlisted item (free text, no price) to the active list.
   * Returns `false` when the name is blank or there is no active list yet
   * (still loading / a failed load), so the caller can keep the user's search
   * context instead of clearing it on an add that was silently dropped.
   */
  addCustomItem(name: string): boolean {
    const list = this.activeList$.value;
    const trimmed = (name ?? "").trim();
    if (!list || trimmed.length === 0) {
      return false;
    }
    this.enqueueMutation(
      list._id,
      () => this.lists.addItem(list._id, { name: trimmed }),
      "GroceryService: add custom item failed",
    );
    return true;
  }

  /**
   * Reports a custom (not-found) term to the shared backlog when the user asks us
   * to source a price for it (the "Request price" action on a custom list item),
   * once per session so re-requesting the same term doesn't inflate the count.
   *
   * Emits `true` when the term is (or already was) reported and `false` when the
   * report failed, so the caller can gate its "requested" confirmation on the
   * result instead of assuming success. A failure never surfaces as an error and
   * re-arms the term, so the user can retry. Cold: nothing is sent until
   * subscribed.
   */
  requestPrice(term: string): Observable<boolean> {
    const key = term.trim().toLowerCase();
    if (key.length === 0) {
      return of(false);
    }
    if (this.reportedNotFound.has(key)) {
      // Already reported this session — treat as done so the caller still
      // shows its confirmation without re-posting.
      return of(true);
    }
    this.reportedNotFound.add(key);
    return this.lists.reportNotFound(term.trim()).pipe(
      map(() => true),
      catchError((e: unknown) => {
        // Un-burn the term so a transient failure does not drop it for the session.
        this.reportedNotFound.delete(key);
        // eslint-disable-next-line no-console
        console.error("GroceryService: report not-found failed", e);
        return of(false);
      }),
    );
  }

  /** Removes an item from the active list, by its server list-item id. */
  removeItem(listItemId: string): void {
    const list = this.activeList$.value;
    if (!list) {
      return;
    }
    // Capture the removed item's catalog id before the async removal so we can
    // drop its session price override on success — otherwise a remove-then-
    // re-add of the same catalog item would replay the stale refreshed price and
    // re-stamp the old priceAsOf onto the fresh row.
    const genericItemId = list.items.find((i) => i._id === listItemId)?.genericItemId;
    this.enqueueMutation(
      list._id,
      () => this.lists.removeItem(list._id, listItemId),
      "GroceryService: remove item failed",
      () => {
        // Drop the removed item's session price override on success. Ordered by
        // the queue, so a remove that is followed by a re-add + refresh of the
        // same catalog item cannot wipe the fresh override — the re-add's write
        // runs strictly after this one.
        if (genericItemId) {
          this.priceOverrides.delete(genericItemId);
        }
      },
    );
  }

  /**
   * Row menu's "Archive this item": splits one item off the active list into
   * its own archived list, so it shows up under Archived Lists (and can be
   * copied back in later) without archiving the rest of the current list.
   */
  archiveItem(listItemId: string): Observable<ShoppingList> {
    const list = this.activeList$.value;
    const item = list?.items.find((i) => i._id === listItemId);
    if (!list || !item) {
      return throwError(() => new Error("no such item on the active list"));
    }
    // Bump before the writes (like add/remove/tick): the create briefly leaves
    // two active lists server-side, so a page-open reload landing in that
    // window must discard its GET rather than adopt the single-item list (or a
    // pre-remove snapshot that still holds the item).
    this.mutationGeneration++;
    return this.lists.createList({ name: item.name, items: [toNewItem(item)] }).pipe(
      switchMap((created) => {
        // The three calls aren't atomic. If the archive fails, the split-off
        // list is left *active*; if the remove fails, the item is both active
        // and archived. Either way delete the split-off list so nothing is
        // left half-done.
        const rollback = (error: unknown): Observable<never> =>
          this.lists.deleteList(created._id).pipe(
            catchError((rollbackError: unknown) => {
              // eslint-disable-next-line no-console
              console.error("GroceryService: archive item rollback failed", rollbackError);
              return of(null);
            }),
            switchMap(() => throwError(() => error)),
          );
        // The backend never trusts `purchased` on create (it always stores
        // false), so re-tick the split-off item before archiving; otherwise a
        // bought item would show up in Archived Lists as "Was unticked". A
        // failure here is covered by the same rollback.
        const ticked = item.purchased
          ? this.lists.setItemPurchased(created._id, created.items[0]._id, true)
          : of(created);
        return ticked.pipe(
          switchMap(() => this.lists.archiveList(created._id)),
          catchError(rollback),
          // The remove goes through the mutation queue (#5090), like every other
          // write to the active list: each response carries the whole list, so
          // an unqueued remove could land after (or before) a concurrent tick
          // and resurrect the archived item or drop the tick. The queue applies
          // the result and also drops it if the list changed meanwhile.
          switchMap(() =>
            this.enqueueAndWait(
              list._id,
              () =>
                this.lists
                  .removeItem(list._id, listItemId)
                  .pipe(
                    catchError((error: unknown) =>
                      this.confirmRemoved(list._id, listItemId, error, rollback),
                    ),
                  ),
              "GroceryService: archive item remove failed",
              () => {
                // Same as removeItem: drop the session price override so a
                // later re-add doesn't replay the stale refreshed price.
                if (item.genericItemId) {
                  this.priceOverrides.delete(item.genericItemId);
                }
              },
            ),
          ),
        );
      }),
      // Bump again once settled: a page-open reload that *started* mid-split
      // read the already-bumped generation, so the up-front bump doesn't stop
      // it adopting the transient single-item list (newest-touched, so
      // lists[0]) or a pre-remove snapshot.
      finalize(() => {
        this.mutationGeneration++;
        // That discarded reload set "loading"; nothing else would clear it on
        // the error path (the list is unchanged and still held).
        this.resolveStatus();
      }),
    );
  }

  /**
   * A failed remove may still have succeeded server-side (e.g. a client-side
   * timeout), so re-read the list before rolling back — deleting the archived
   * copy of an item that is already gone from the list would lose it. Removed
   * after all: carry on with the fresh list. Still there: roll back. Can't
   * tell: keep the archived copy (a duplicate beats a loss) and fail.
   */
  private confirmRemoved(
    listId: string,
    listItemId: string,
    error: unknown,
    rollback: (error: unknown) => Observable<never>,
  ): Observable<ShoppingList> {
    return this.lists.getList(listId).pipe(
      catchError((checkError: unknown) => {
        // eslint-disable-next-line no-console
        console.error("GroceryService: archive item remove check failed", checkError);
        return throwError(() => error);
      }),
      switchMap((current) =>
        current.items.some((i) => i._id === listItemId) ? rollback(error) : of(current),
      ),
    );
  }

  /** Ticks an item off (or un-ticks it). */
  setPurchased(listItemId: string, purchased: boolean): void {
    const list = this.activeList$.value;
    if (!list) {
      return;
    }
    // Queued like add/remove so ticks apply in order and an in-flight page-open
    // reload (guarded by the generation bump inside enqueueMutation) can't apply
    // a pre-tick GET and undo the tick.
    this.enqueueMutation(
      list._id,
      () => this.lists.setItemPurchased(list._id, listItemId, purchased),
      "GroceryService: tick item failed",
    );
  }

  /**
   * Merges freshly-fetched prices into a list item after a live refresh —
   * in-memory only, so the row and totals reflect it for the session. (The
   * stored snapshot isn't rewritten; the fresh price is persisted to the shared
   * catalog separately via {@link persistPrices}.) The merge is recorded as a
   * session override so it survives a later add/remove that replaces the active
   * list with the server's (pre-refresh) copy. No-op when the catalog id is not
   * on the current list, so a refresh that finishes after the row was removed
   * cannot stamp a stale override onto a later re-add.
   */
  updateItemPrices(
    genericItemId: string,
    prices: Partial<Record<GroceryStoreKey, number>>,
    priceAsOf: Partial<Record<GroceryStoreKey, string>>,
  ): void {
    const list = this.activeList$.value;
    if (!list || !list.items.some((i) => i.genericItemId === genericItemId)) {
      return;
    }
    const existing = this.priceOverrides.get(genericItemId);
    this.priceOverrides.set(genericItemId, {
      prices: { ...(existing?.prices ?? {}), ...prices },
      priceAsOf: { ...(existing?.priceAsOf ?? {}), ...priceAsOf },
    });
    this.setActiveList(list);
  }

  /**
   * Archives the active list (kept for history / copy-over) and starts its
   * replacement in one step, returning the freshly-created active list.
   *
   * Kept as a single method because the archive→create sequence must not be
   * left half-done: it is treated as a mutation just like add/remove/tick, so
   * every guard the load pipeline relies on holds.
   * - {@link mutationGeneration} is bumped up-front, so a page-open reload that
   *   is still in flight discards its (pre-archive) GET instead of resurrecting
   *   the archived list.
   * - the follow-on create is routed through {@link createInFlight$} (the same
   *   `shareReplay` {@link createActiveList} uses), so a reload that raced this
   *   archive and then sees `[]` *joins* this create instead of POSTing a second
   *   active list. (Archive is the one mutation where `getLists("active") → []`
   *   is the expected state, so that window is real.)
   * - state moves only through {@link setActiveList} (status + price overrides),
   *   never a raw `activeList$.next`, so `status$` never lies about a null list.
   *
   * On a create failure *after* the archive persisted, the list is already
   * cleared; the status is set to `"error"` (not left on a permanent loading
   * spinner) and the error is re-thrown so the caller can surface it — the
   * archived list's un-ticked items remain recoverable from Past lists.
   *
   * @param carryForward carry the archived list's un-ticked items into the new list
   */
  archiveActiveAndStartNew(carryForward: boolean): Observable<ShoppingList> {
    const list = this.activeList$.value;
    if (!list) {
      return throwError(() => new Error("no active list to archive"));
    }
    // Bump before the writes (like add/remove/tick) so an in-flight page-open
    // reload can't apply a pre-archive GET and revive the archived list.
    this.mutationGeneration++;
    return this.lists.archiveList(list._id).pipe(
      switchMap((archived) => {
        // Clear the view (status -> "loading") only now the archive persisted;
        // clearing up-front would blank a still-active list on a transient
        // failure.
        this.setActiveList(null);
        const unticked = archived.items.filter((i) => !i.purchased);
        const items = carryForward && unticked.length > 0 ? unticked.map(toNewItem) : [];
        // Archiving is a mass-remove, so drop the session price overrides of every
        // item leaving with the archived list — exactly as removeItem does for a
        // single row. Without this a later re-add of the same catalog item would
        // replay the stale refreshed price and re-stamp its old priceAsOf onto the
        // fresh row. Overrides for carried-forward items are kept: those rows are
        // still on screen, so their just-refreshed price must not revert.
        const carried = new Set(items.map((i) => i.genericItemId).filter(Boolean));
        for (const id of this.priceOverrides.keys()) {
          if (!carried.has(id)) {
            this.priceOverrides.delete(id);
          }
        }
        // Publish the follow-on create as the in-flight create (replacing any
        // stale "first list" replay) so a racing empty getLists joins it rather
        // than POSTing a duplicate active list. Nulled on error so a failed
        // create can retry; left on success — the load pipeline nulls it once a
        // getLists returns the list.
        this.createInFlight$ = this.lists.createList({ items }).pipe(
          tap({
            error: () => (this.createInFlight$ = null),
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
        return this.createInFlight$;
      }),
      tap((fresh) => this.setActiveList(fresh)),
      catchError((error: unknown) => {
        // Only surface an error state when the list is actually gone (the create
        // failed after the archive persisted) — if the archive PATCH itself
        // failed the list is still held and still "loaded", so don't lie. In the
        // gone case this stops the page sitting on a loading spinner forever.
        if (this.activeList$.value === null) {
          this.status$.next("error");
        }
        return throwError(() => error);
      }),
    );
  }

  /** All of the user's archived lists (for the "Past lists" section). */
  getArchivedLists(): Observable<ShoppingList[]> {
    return this.lists.getLists("archived");
  }

  /** Delete an entire archived (past) list. */
  deleteArchivedList(listId: string): Observable<{ deleted: boolean }> {
    return this.lists.deleteList(listId);
  }

  /**
   * Copies an archived list's items into the current active list. Items whose
   * catalog id is already on the active list are skipped (same dedup rule as
   * {@link addItem}), so copying an overlapping list doesn't create duplicate
   * rows; custom (id-less) items are deduped by normalised name
   * ({@link listItemDedupKey}). State is refreshed after
   * every item — via {@link setActiveList}, so live-refresh price overrides
   * survive — so a mid-sequence failure still reflects the items added so far.
   */
  copyListIntoActive(list: ShoppingList): Observable<ShoppingList> {
    const active = this.activeList$.value;
    if (!active) {
      // No active list in state. Only start a fresh one when the load actually
      // failed ("error" — the recovery case). While a load is still in flight
      // ("loading") the server may already hold a list this GET is about to
      // return; creating here would POST a duplicate, and the generation bump
      // in startNewActiveList would then discard that GET, leaving two active
      // lists. Refuse instead — the caller keeps the past list and copy works
      // once the load settles.
      if (this.status$.value === "loading") {
        return throwError(() => new Error("active list still loading; try again"));
      }
      return this.startNewActiveList(list.items.map(toNewItem));
    }
    // Dedup against the items already on the list and within the copy set itself.
    const seen = new Set(active.items.map(listItemDedupKey));
    const toAdd: NewShoppingListItem[] = [];
    for (const item of list.items) {
      const key = listItemDedupKey(item);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      toAdd.push(toNewItem(item));
    }
    if (toAdd.length === 0) {
      return of(active);
    }
    // Copy is a write like add/remove/tick: bump up-front so an in-flight
    // page-open reload discards its (pre-copy) GET instead of overwriting the
    // list and dropping the copied items until the next reload.
    this.mutationGeneration++;
    return from(toAdd).pipe(
      concatMap((item) =>
        this.lists.addItem(active._id, item).pipe(tap((updated) => this.setActiveList(updated))),
      ),
      last(),
    );
  }

  /**
   * Creates the user's active list seeded with `items` and publishes it. Used
   * by {@link copyListIntoActive} when a copy happens while there is no active
   * list (a failed or still-pending load — the recovery case).
   *
   * Mirrors the create half of {@link archiveActiveAndStartNew} rather than
   * calling {@link ShoppingListService.createList} directly: {@link
   * mutationGeneration} is bumped up-front, and the create is routed through
   * {@link createInFlight$} (the same `shareReplay` {@link createActiveList}
   * uses), so a load that raced this and then sees `[]` *joins* this create
   * instead of POSTing a second active list. Nulled on error so a failed
   * create can retry; left on success — the load pipeline nulls it once a
   * getLists returns the list.
   */
  private startNewActiveList(items: NewShoppingListItem[]): Observable<ShoppingList> {
    this.mutationGeneration++;
    this.createInFlight$ = this.lists.createList({ items }).pipe(
      tap({
        error: () => (this.createInFlight$ = null),
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.createInFlight$.pipe(
      tap((fresh) => this.setActiveList(fresh)),
      catchError((error: unknown) => {
        // The create failed and no list was published. A page-open reload that
        // raced this was discarded by the generation bump, so nothing else will
        // move status off "loading" — set "error" (like {@link
        // archiveActiveAndStartNew}) so the view shows a recoverable error
        // instead of a permanent spinner. Re-thrown so the caller can toast.
        if (this.activeList$.value === null) {
          this.status$.next("error");
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Copies a single archived-list item into the current active list — the
   * per-item "Add" action in the Archived Lists view (same dedup rule as
   * {@link copyListIntoActive}). The write goes through the mutation queue so it
   * can't be reordered against a concurrent tick/remove/add (#5090); the returned
   * cold observable emits the updated list once applied, or errors if the write
   * fails or is dropped.
   */
  copyItemIntoActive(item: ShoppingListItem): Observable<ShoppingList> {
    const active = this.activeList$.value;
    if (!active) {
      // Same "loading" guard as copyListIntoActive: creating while a load is in
      // flight could POST a duplicate active list.
      if (this.status$.value === "loading") {
        return throwError(() => new Error("active list still loading; try again"));
      }
      return this.startNewActiveList([toNewItem(item)]);
    }
    const key = listItemDedupKey(item);
    if (active.items.some((i) => listItemDedupKey(i) === key)) {
      return of(active);
    }
    return this.enqueueAndWait(
      active._id,
      () => this.lists.addItem(active._id, toNewItem(item)),
      "GroceryService: copy item into active list failed",
    );
  }

  /** Decorates a set of items into render-ready rows (used for search-result re-decoration). */
  decorateRows(items: GroceryItem[]): GroceryResultRow[] {
    return buildResultRows(items);
  }

  /**
   * Persists freshly-fetched prices for one item to the shared catalog via
   * `POST /v1/grocery/prices`. Returns what the server actually saved
   * (`applied`) versus refused (`rejected`).
   */
  persistPrices(
    genericItemId: string,
    prices: Partial<Record<GroceryStoreKey, number>>,
  ): Observable<PricePersistResult> {
    return withGroceryAuth(this.apiService, (headers) =>
      this.http.post<PricePersistResult>(
        `${getGroceryConfig().url}${getGroceryConfig().endpoint.prices}`,
        { genericItemId, prices },
        { headers },
      ),
    ).pipe(timeout(GROCERY_TIMEOUT_MS));
  }
}
